import { useCallback, useEffect, useState } from 'react';
import { Card, Group, Progress, Stack, Text, Title } from '@mantine/core';
import { api, errorMessage } from '../api/client';
import type { UsageView } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ErrorNote } from '../components/ErrorNote';
import { Loading } from '../components/Loading';
import { PlanSection } from '../components/PlanSection';

/**
 * The plan, and where the month has got to, for anybody in the newsroom who wants to know.
 *
 * Two subjects on one screen because they are one question asked twice: what are we on, and how
 * much of it have we used. They load separately, so a publisher whose plan cannot be fetched still
 * gets their numbers and the other way round, and neither section can take the other down.
 *
 * This screen is deliberately not a meter. The pricing page says the allowance is there to place a
 * publisher on a tier rather than to be rationed, and that we would rather they used it heavily
 * than watched a counter, and the product only works if editors put interactions everywhere they
 * fit. So there is one bar in one colour at every level, nothing turns amber at 80 per cent, and a
 * month that has gone past the allowance is written up as a publisher doing well rather than as a
 * fault to clear. Anyone tempted to add urgency here should read the pricing page first: it is a
 * promise we have already made in public.
 *
 * The one thing that is stated plainly is the brand limit, because that one is enforced. Somebody
 * who finds out at the moment they are refused has been surprised by us, and it is cheap to say it
 * here first.
 *
 * Any member can read this. An editor deciding what to publish this week is exactly the person the
 * numbers are for, and a number only an owner can see is a number an editor will interrupt an
 * owner for.
 */
export function Billing() {
  const { org } = useAuth();
  const orgId = org?.org_id ?? null;
  const orgName = org?.name ?? 'this publisher';

  const [usage, setUsage] = useState<UsageView | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(() => {
    if (orgId === null) return;
    setProblem(null);
    setUsage(null);
    api
      .usage(orgId)
      .then(setUsage)
      .catch((error: unknown) => setProblem(errorMessage(error)));
  }, [orgId]);

  useEffect(load, [load]);

  return (
    <>
      <Title order={1} size="h2">
        Billing and usage
      </Title>
      <Text size="lg" mt="sm" mb="lg">
        What {orgName} is on, what it has used this month, and what the plan covers. The allowance
        is here to put you on a plan, not to be rationed: we would rather you used it heavily than
        watched a counter.
      </Text>

      <PlanSection />

      {problem !== null && <ErrorNote message={problem} onRetry={load} />}
      {problem === null && usage === null && <Loading label="Loading your usage" />}

      {usage !== null && (
        <Stack gap="md" mt="md">
          <ThisMonth usage={usage} />
          <Brands usage={usage} />
          <LastMonth usage={usage} />
        </Stack>
      )}
    </>
  );
}

/** This month's total, the plan it sits under, and the bar when there is one to draw. */
function ThisMonth({ usage }: { usage: UsageView }) {
  return (
    <Card component="section" withBorder radius="lg" p="lg">
      {/*
       * The plan is not repeated here. It is named and priced in the card above this one, and a
       * screen that says the same thing twice makes somebody check whether the two agree.
       */}
      <Title order={2} size="h3">
        This month
      </Title>

      <Text size="sm" c="dimmed" mt={2}>
        {usage.month_label}
      </Text>

      <Group align="baseline" gap="xs" mt="md">
        <Text ff="heading" fw={600} fz={36} lh={1} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {count(usage.completions)}
        </Text>
        <Text size="sm" c="dimmed">
          {usage.completions === 1 ? 'response' : 'responses'} so far
        </Text>
      </Group>

      <AllowanceBar usage={usage} />

      <Text mt="md">{usage.summary}</Text>

      {usage.completions === 0 && (
        <Text mt="xs">
          Nothing has been counted yet this month. A response lands here the moment a reader
          finishes an interaction on one of your articles.
        </Text>
      )}

      {usage.over_allowance && <OverAllowance />}

      <Text size="sm" c="dimmed" mt="md">
        A response is one reader finishing one interaction, so a ten question quiz counts once.
        Impressions, starts and anything a reader abandons count for nothing. The month runs to the
        calendar in London and starts again on the first.
      </Text>
    </Card>
  );
}

/**
 * The bar, or the sentence that goes where a bar cannot.
 *
 * A null fraction means there is no published number to divide by, which is the network plan.
 * Drawing an empty bar there would tell a customer they had used nothing, and drawing a full one
 * would be worse, so the bar is left out and the position is said in words instead.
 *
 * The colour never changes. It is the same violet at 4 per cent and at 140 per cent, because a bar
 * that changes colour as it fills is a bar telling an editor to stop, and that is the opposite of
 * what this product needs them to do.
 */
function AllowanceBar({ usage }: { usage: UsageView }) {
  if (usage.used_fraction === null || usage.allowance === null) {
    return (
      <Text size="sm" c="dimmed" mt="sm">
        The {usage.plan_label} plan's monthly number is the one we agreed with you rather than a
        published figure, so there is nothing here to draw a bar against. Ask us any time and we
        will tell you where the month stands against it.
      </Text>
    );
  }

  const percent = Math.round(usage.used_fraction * 100);
  // The fill stops at the end of the bar, and the real figure is in the caption underneath, so a
  // month that has gone well past the allowance is still readable as a number.
  const filled = Math.min(100, Math.max(0, percent));

  return (
    <>
      <Progress
        value={filled}
        color="violet"
        size="lg"
        radius="xl"
        mt="md"
        aria-label={`${count(usage.completions)} of ${count(usage.allowance)} responses this month`}
      />
      <Group justify="space-between" gap="xs" mt={6} wrap="wrap">
        <Text size="sm" c="dimmed">
          {percent}% of what the {usage.plan_label} plan includes
        </Text>
        <Text size="sm" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {count(usage.allowance)} included
        </Text>
      </Group>
    </>
  );
}

/**
 * What we say once a month has gone past the allowance.
 *
 * A plain card rather than an Alert on purpose. Mantine's Alert carries role="alert", which is an
 * assertive live region: it would have a screen reader interrupt somebody to announce a good month.
 * There is also no warning colour here, and there should never be one. The words are the pricing
 * page's own promise, because a publisher who read that page and then saw something sterner here
 * would be right to trust neither.
 */
function OverAllowance() {
  return (
    <Card withBorder radius="md" p="md" mt="md">
      <Title order={3} size="h5">
        Past the allowance, and nothing has changed
      </Title>
      <Text size="sm" mt={6}>
        A month past the allowance is a publisher doing well, not a problem to fix. Everything
        carries on exactly as it is: every poll, quiz, survey and form keeps collecting, no reader
        sees anything different, and nothing goes dark in the middle of an article. We will get in
        touch about the plan that fits you now, and nothing is charged that you have not agreed to
        first.
      </Text>
    </Card>
  );
}

/**
 * The one allowance that is enforced, said out loud before it bites.
 *
 * Responses are never refused. Brands are, at the moment somebody adds one, so this card exists to
 * make sure that refusal is something a publisher already knew about rather than something the
 * product springs on them halfway through setting up a new masthead.
 */
function Brands({ usage }: { usage: UsageView }) {
  const unlimited = usage.brands_allowed === null;
  const atLimit = usage.brands_allowed !== null && usage.brands_used >= usage.brands_allowed;

  return (
    <Card component="section" withBorder radius="lg" p="lg">
      <Title order={2} size="h3">
        Brands
      </Title>

      <Group align="baseline" gap="xs" mt="sm">
        <Text ff="heading" fw={600} fz={28} lh={1} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {unlimited
            ? count(usage.brands_used)
            : `${count(usage.brands_used)} of ${count(usage.brands_allowed ?? 0)}`}
        </Text>
        <Text size="sm" c="dimmed">
          {usage.brands_used === 1 && unlimited ? 'brand' : 'brands'}
        </Text>
      </Group>

      {unlimited ? (
        <Text mt="sm">
          The {usage.plan_label} plan does not cap them, so add a masthead whenever you take one on.
        </Text>
      ) : (
        <Text mt="sm">
          The {usage.plan_label} plan covers {plural(usage.brands_allowed ?? 0, 'brand')}.
          {atLimit
            ? ' You are using all of them, so the next one would be refused. Get in touch and we will move you onto a plan with more.'
            : ''}
        </Text>
      )}

      <Text size="sm" c="dimmed" mt="sm">
        This is the only limit we enforce. Adding a brand beyond what the plan covers is refused
        there and then, with a message naming the plan, because adding a masthead is a deliberate
        thing somebody does while looking at a screen and can read. Responses are never treated that
        way.
      </Text>
    </Card>
  );
}

/** Last month, so "is this month unusual?" can be answered without opening a spreadsheet. */
function LastMonth({ usage }: { usage: UsageView }) {
  const previous = usage.previous_month;

  return (
    <Card component="section" withBorder radius="lg" p="lg">
      <Title order={2} size="h3">
        Last month
      </Title>
      <Group align="baseline" gap="xs" mt="sm">
        <Text ff="heading" fw={600} fz={28} lh={1} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {count(previous.completions)}
        </Text>
        <Text size="sm" c="dimmed">
          {previous.completions === 1 ? 'response' : 'responses'} in {previous.month_label}
        </Text>
      </Group>
      <Text mt="sm">
        {previous.completions === 0
          ? `Nothing was counted in ${previous.month_label}, so there is nothing to compare this month against yet.`
          : `${usage.month_label} is still running, so the two only compare like for like once it has finished.`}
      </Text>
    </Card>
  );
}

/** Thousands separated the way a British newsroom writes them. */
function count(value: number): string {
  return value.toLocaleString('en-GB');
}

function plural(value: number, noun: string): string {
  return `${count(value)} ${noun}${value === 1 ? '' : 's'}`;
}
